import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { Plus, Search, User } from "lucide-react";
import { toast } from "sonner";

function NewPatientDrawer({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", gender: "female", dob: "",
    address: "", medical_history: "", allergies: "", dpdp_consent: false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.dpdp_consent) {
      toast.error("DPDP consent is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, email: form.email || null, dob: form.dob || null };
      const { data } = await api.post("/patients", payload);
      toast.success(`Patient created · ${data.mrn}`);
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const label = "text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1 block";
  const input = "w-full bg-white border border-[#E5E5E2] text-sm px-3 py-2 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0F4C3A] focus:border-[#0F4C3A]";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="new-patient-drawer">
      <form onSubmit={submit} className="w-full max-w-lg bg-white h-full overflow-auto shadow-2xl">
        <div className="px-6 py-5 border-b border-[#E5E5E2] flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="font-display text-xl font-bold tracking-tight">New Patient</h2>
          <button type="button" onClick={onClose} className="text-sm text-[#52525B] hover:text-[#1C1C1C]" data-testid="close-drawer-btn">Close</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className={label}>Full Name *</label>
            <input required data-testid="patient-full-name" className={input} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Phone *</label>
              <input required data-testid="patient-phone" className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div>
              <label className={label}>Email</label>
              <input type="email" data-testid="patient-email" className={input} value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Gender</label>
              <select data-testid="patient-gender" className={input} value={form.gender} onChange={(e) => set("gender", e.target.value)}>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className={label}>Date of birth</label>
              <input type="date" data-testid="patient-dob" className={input} value={form.dob} onChange={(e) => set("dob", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={label}>Address</label>
            <textarea rows={2} className={input} value={form.address} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div>
            <label className={label}>Medical history</label>
            <textarea rows={2} data-testid="patient-medical" className={input} value={form.medical_history} onChange={(e) => set("medical_history", e.target.value)} />
          </div>
          <div>
            <label className={label}>Allergies</label>
            <input className={input} value={form.allergies} onChange={(e) => set("allergies", e.target.value)} />
          </div>
          <label className="flex items-start gap-3 p-3 bg-[#F4F4F5] border border-[#E5E5E2] rounded-sm cursor-pointer">
            <input type="checkbox" data-testid="patient-consent" className="mt-1" checked={form.dpdp_consent} onChange={(e) => set("dpdp_consent", e.target.checked)} />
            <span className="text-xs text-[#52525B] leading-relaxed">
              <b className="text-[#1C1C1C]">DPDP consent:</b> The patient has been informed and consents to the collection
              and processing of their personal and medical data for treatment, billing and legally required purposes.
            </span>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-[#E5E5E2] flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 border border-[#E5E5E2] rounded-md hover:bg-[#F4F4F5]">Cancel</button>
          <button type="submit" data-testid="save-patient-btn" disabled={saving} className="text-sm px-4 py-2 bg-[#0F4C3A] text-white rounded-md hover:bg-[#16654F] disabled:opacity-60">
            {saving ? "Saving…" : "Create patient"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Patients() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = async (query = "") => {
    const { data } = await api.get("/patients", { params: query ? { q: query } : {} });
    setList(data);
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q);
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#E27B58] font-semibold mb-1">Directory</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Patients</h1>
          <p className="text-sm text-[#52525B] mt-1">{list.length} records</p>
        </div>
        <button data-testid="new-patient-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-[#0F4C3A] text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-[#16654F]">
          <Plus className="w-4 h-4" strokeWidth={2.5} /> New Patient
        </button>
      </div>

      <form onSubmit={onSearch} className="mb-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input
            data-testid="patient-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone or MRN…"
            className="w-full pl-9 pr-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#0F4C3A]"
          />
        </div>
      </form>

      <div className="bg-white border border-[#E5E5E2] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#52525B] bg-[#FDFDFB]">
              <th className="p-3 border-b border-[#E5E5E2]">MRN</th>
              <th className="p-3 border-b border-[#E5E5E2]">Name</th>
              <th className="p-3 border-b border-[#E5E5E2]">Phone</th>
              <th className="p-3 border-b border-[#E5E5E2]">Gender</th>
              <th className="p-3 border-b border-[#E5E5E2]">Registered</th>
              <th className="p-3 border-b border-[#E5E5E2]"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr>
                <td colSpan={6} className="p-10 text-center text-[#52525B]">
                  No patients yet. Click <b>New Patient</b> to register the first one.
                </td>
              </tr>
            )}
            {list.map((p) => (
              <tr key={p.id} className="hover:bg-[#FDFDFB]" data-testid={`patient-row-${p.mrn}`}>
                <td className="p-3 border-b border-[#E5E5E2] font-mono-num text-[#52525B]">{p.mrn}</td>
                <td className="p-3 border-b border-[#E5E5E2]">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-[#F4F4F5] flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-[#52525B]" />
                    </div>
                    <span className="font-medium">{p.full_name}</span>
                  </div>
                </td>
                <td className="p-3 border-b border-[#E5E5E2] font-mono-num">{p.phone}</td>
                <td className="p-3 border-b border-[#E5E5E2] capitalize text-[#52525B]">{p.gender || "—"}</td>
                <td className="p-3 border-b border-[#E5E5E2] text-[#52525B]">{new Date(p.created_at).toLocaleDateString("en-IN")}</td>
                <td className="p-3 border-b border-[#E5E5E2] text-right">
                  <Link to={`/patients/${p.id}`} data-testid={`open-patient-${p.mrn}`} className="text-[#0F4C3A] hover:text-[#16654F] font-semibold text-xs">
                    Open chart →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewPatientDrawer open={open} onClose={() => setOpen(false)} onCreated={(p) => setList((s) => [p, ...s])} />
    </div>
  );
}
